import { Body, Button, Form, Spacer, validators } from '@spritz-finance/ui'
import React, { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { useForgotPassword } from '../../app/auth'
import {
    AuthenticationBottomLink,
    AuthenticationBottomText,
    FormContent,
} from '../AuthenticationStyled'
import { ForgotPasswordBottomLink, ForgotPasswordBottomText } from './ForgotPasswordStyled'

enum ForgotPasswordState {
    EnterEmail,
    EnterNewPassword,
    PasswordChangeCompleted,
}

interface EmailForm {
    email: string
}

interface EnterEmailProps {
    onComplete: (email?: string) => void
}

interface EnterNewPasswordProps {
    email: string
    onComplete: (email?: string) => void
}

interface NewPasswordForm {
    code: string
    password: string
    confirmPassword: string
}

const Completed = () => {
    const navigate = useNavigate()

    const goHome = () => navigate('/home')

    return (
        <>
            <Body.Med size={14}>Your password has been changed. Yay!</Body.Med>
            <Spacer height={32} />
            <Button.CTA onClick={goHome}>Go to Home Screen</Button.CTA>
            <Spacer height={48} />
        </>
    )
}

const EnterEmail: React.VFC<EnterEmailProps> = ({ onComplete }) => {
    const { sendForgotPasswordEmail, loading, error } = useForgotPassword()
    const navigate = useNavigate()

    const methods = useForm<EmailForm>({
        reValidateMode: 'onBlur',
    })

    useEffect(() => {
        if (error) {
            methods.setError('email', error)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error])

    const onSubmit: SubmitHandler<EmailForm> = async ({ email }) => {
        const result = await sendForgotPasswordEmail(email)
        if (result) {
            onComplete(email)
        }
    }

    return (
        <>
            <Body.Med>Enter your email address to reset your password.</Body.Med>
            <Spacer height={32} />
            <Form<EmailForm> onSubmit={onSubmit} width="auto" methods={methods}>
                <FormContent>
                    <Form.Input
                        name="email"
                        placeholder="Email address"
                        validation={{
                            required: true,
                            validate: {
                                ...validators.email,
                            },
                        }}
                    />
                    <Button.CTA loading={loading} type="submit">
                        Send email
                    </Button.CTA>
                </FormContent>
            </Form>
            <AuthenticationBottomText>
                <AuthenticationBottomLink onClick={() => navigate('/signin')}>
                    Back to Sign In
                </AuthenticationBottomLink>
            </AuthenticationBottomText>
        </>
    )
}

const EnterNewPassword: React.VFC<EnterNewPasswordProps> = ({ onComplete, email }) => {
    const { confirmNewPassword, sendForgotPasswordEmail, loading, error } = useForgotPassword()

    const [didResend, setDidResend] = useState<boolean>(false)

    const methods = useForm<NewPasswordForm>({
        reValidateMode: 'onBlur',
    })

    useEffect(() => {
        if (!error) {
            return
        }

        let field
        switch (error.type) {
            case 'CodeMismatchException':
                field = 'code'
                break
            case 'InvalidPasswordException':
                field = 'password'
                break
            default:
                field = 'global'
        }
        methods.setError(field, error)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error])

    const onSubmit: SubmitHandler<NewPasswordForm> = async (data) => {
        const result = await confirmNewPassword(email, data.code, data.password)
        if (result) {
            onComplete()
        }
    }

    const resendConfirmation = async () => {
        await sendForgotPasswordEmail(email)
        setDidResend(true)
    }

    return (
        <>
            <Body.Med>
                We just sent you an email with a verification code. Enter that code and a new
                password, and you're done!
            </Body.Med>
            <Spacer height={32} />
            <Form<NewPasswordForm> onSubmit={onSubmit} methods={methods} width="auto">
                <FormContent>
                    <Form.Input
                        name="code"
                        placeholder="Verification Code"
                        autoComplete="off"
                        validation={{
                            required: true,
                            pattern: {
                                value: /\d{6}/,
                                message: 'Invalid code',
                            },
                        }}
                    />
                    <Spacer height={16} />
                    <Form.Input
                        name="password"
                        type="password"
                        placeholder="New Password"
                        autoComplete="off"
                        validation={{ required: true }}
                    />
                    <Form.Input
                        name="confirmPassword"
                        type="password"
                        confirm="password"
                        placeholder="Confirm New Password"
                        confirmMessage="Passwords do not match"
                        autoComplete="off"
                    />
                    <Form.FieldError name="global" />
                    <Spacer height={8} />
                    <Button.CTA
                        type="submit"
                        loading={loading}
                        onClick={() => methods.clearErrors()}
                    >
                        Reset password
                    </Button.CTA>
                </FormContent>
            </Form>

            <ForgotPasswordBottomText>
                {!didResend ? "Didn't get an email?" : "Sent. Still haven't gotten an email?"}
                <ForgotPasswordBottomLink onClick={resendConfirmation}>
                    Send Again
                </ForgotPasswordBottomLink>
            </ForgotPasswordBottomText>
        </>
    )
}

const ForgotPasswordForm = () => {
    const [pageState, setPageState] = useState<ForgotPasswordState>(ForgotPasswordState.EnterEmail)

    const [email, setEmail] = useState<string>()

    const sendToNextState = (email?: string) => {
        if (email) {
            setEmail(email)
        }
        if (pageState === ForgotPasswordState.EnterEmail) {
            setPageState(ForgotPasswordState.EnterNewPassword)
        } else if (pageState === ForgotPasswordState.EnterNewPassword) {
            setPageState(ForgotPasswordState.PasswordChangeCompleted)
        }
    }

    return (
        <>
            {pageState === ForgotPasswordState.EnterEmail && (
                <EnterEmail onComplete={sendToNextState} />
            )}
            {pageState === ForgotPasswordState.EnterNewPassword && email && (
                <EnterNewPassword onComplete={sendToNextState} email={email} />
            )}

            {pageState === ForgotPasswordState.PasswordChangeCompleted && <Completed />}
        </>
    )
}

export default ForgotPasswordForm
